import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Activity, AlertTriangle, 
  Target, Clock, CheckCircle, XCircle, Download, RefreshCw 
} from 'lucide-react';
import { analyticsAPI } from '../lib/api';

const COLORS = ['#00F0FF', '#FF6B00', '#00FF88', '#FF3366', '#FFD700', '#9966FF'];

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [riskTrends, setRiskTrends] = useState([]);
  const [networkStats, setNetworkStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overview, risks, network] = await Promise.all([
        analyticsAPI.getOverview(dateRange),
        analyticsAPI.getRiskTrends(dateRange),
        analyticsAPI.getNetworkStats()
      ]);
      setAnalyticsData(overview);
      setRiskTrends(risks.trends || []);
      setNetworkStats(network);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toFixed(0) || '0';
  };

  const renderMetricCard = (title, value, change, icon, color) => (
    <Card className="bg-gray-900/50 border-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{formatNumber(value)}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-1 text-xs ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {Math.abs(change).toFixed(1)}% vs last period
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Advanced Analytics</h1>
          <p className="text-gray-400 text-sm mt-1">Comprehensive intelligence insights and trends</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40 bg-gray-900 border-gray-700">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} className="border-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="border-gray-700">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {renderMetricCard(
          'Total Transactions',
          analyticsData?.summary?.total_transactions || 0,
          8,
          <Activity className="w-5 h-5 text-cyan-400" />,
          'bg-cyan-500/10'
        )}
        {renderMetricCard(
          'Flagged',
          analyticsData?.summary?.flagged_transactions || 0,
          -5,
          <AlertTriangle className="w-5 h-5 text-orange-400" />,
          'bg-orange-500/10'
        )}
        {renderMetricCard(
          'Flag Rate',
          analyticsData?.summary?.flagged_rate || 0,
          undefined,
          <Target className="w-5 h-5 text-red-400" />,
          'bg-red-500/10'
        )}
        {renderMetricCard(
          'Total Alerts',
          analyticsData?.summary?.total_alerts || 0,
          15,
          <AlertTriangle className="w-5 h-5 text-yellow-400" />,
          'bg-yellow-500/10'
        )}
        {renderMetricCard(
          'Resolved',
          analyticsData?.summary?.resolved_alerts || 0,
          12,
          <CheckCircle className="w-5 h-5 text-green-400" />,
          'bg-green-500/10'
        )}
        {renderMetricCard(
          'Resolution Rate',
          analyticsData?.summary?.resolution_rate || 0,
          undefined,
          <Clock className="w-5 h-5 text-purple-400" />,
          'bg-purple-500/10'
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-900/50 border border-gray-800">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">Risk Analysis</TabsTrigger>
          <TabsTrigger value="network" data-testid="tab-network">Network</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Transaction Trend */}
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Transaction Volume Trend</CardTitle>
                <CardDescription>Daily transaction counts over {dateRange} days</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analyticsData?.transaction_trends || []}>
                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00F0FF" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#00F0FF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis stroke="#666" tick={{fontSize: 11}} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#00F0FF" fill="url(#colorCount)" name="Transactions" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Alert Status Distribution */}
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Alert Status Distribution</CardTitle>
                <CardDescription>Current alert pipeline status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(analyticsData?.alert_status_distribution || {}).map(([name, value]) => ({name, value}))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({name, value}) => `${name}: ${value}`}
                    >
                      {Object.keys(analyticsData?.alert_status_distribution || {}).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Entity Risk Distribution */}
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Entity Risk by Type</CardTitle>
              <CardDescription>Distribution of risk levels across entity types</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData?.entity_risk_distribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="_id.entity_type" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                  <Legend />
                  <Bar dataKey="count" fill="#00F0FF" name="Count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Transaction Amount Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={analyticsData?.transaction_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis yAxisId="left" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis yAxisId="right" orientation="right" stroke="#666" tick={{fontSize: 11}} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" fill="#00F0FF" name="Count" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="total_amount" stroke="#FF6B00" name="Total Amount" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Flagged vs Clean Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analyticsData?.transaction_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis stroke="#666" tick={{fontSize: 11}} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Legend />
                    <Area type="monotone" dataKey="flagged" stackId="1" stroke="#FF3366" fill="#FF3366" fillOpacity={0.5} name="Flagged" />
                    <Area type="monotone" dataKey="count" stackId="2" stroke="#00FF88" fill="#00FF88" fillOpacity={0.3} name="Total" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Risk Tab */}
        <TabsContent value="risk" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Risk Score Trends</CardTitle>
                <CardDescription>Average and maximum risk scores over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={riskTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis stroke="#666" tick={{fontSize: 11}} domain={[0, 100]} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Legend />
                    <Line type="monotone" dataKey="avg_risk_score" stroke="#00F0FF" name="Avg Risk" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="max_risk_score" stroke="#FF3366" name="Max Risk" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">High Risk Activity</CardTitle>
                <CardDescription>Number of high-risk transactions by day</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={riskTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#666" tick={{fontSize: 11}} />
                    <YAxis stroke="#666" tick={{fontSize: 11}} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Bar dataKey="high_risk_count" fill="#FF6B00" name="High Risk Count" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Network Overview</CardTitle>
                <CardDescription>Entity relationship statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-cyan-400">{formatNumber(networkStats?.total_entities || 0)}</p>
                    <p className="text-gray-400 text-sm">Total Entities</p>
                  </div>
                  <div className="bg-gray-800/50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-orange-400">{formatNumber(networkStats?.total_links || 0)}</p>
                    <p className="text-gray-400 text-sm">Total Links</p>
                  </div>
                </div>
                
                <div className="mt-6">
                  <h4 className="text-white text-sm font-medium mb-3">Link Types Distribution</h4>
                  <div className="space-y-2">
                    {Object.entries(networkStats?.link_type_distribution || {}).map(([type, data]) => (
                      <div key={type} className="flex items-center justify-between text-sm">
                        <span className="text-gray-400 capitalize">{type}</span>
                        <span className="text-white">{data.count} links</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Top Connected Entities</CardTitle>
                <CardDescription>Entities with most connections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(networkStats?.top_connected_entities || []).slice(0, 8).map((entity, idx) => (
                    <div key={entity.entity_id} className="flex items-center justify-between bg-gray-800/30 p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500 text-sm w-6">{idx + 1}</span>
                        <div>
                          <p className="text-white text-sm font-medium">{entity.name}</p>
                          <p className="text-gray-400 text-xs">{entity.entity_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-medium ${
                          entity.risk_level === 'critical' ? 'text-red-400' :
                          entity.risk_level === 'high' ? 'text-orange-400' :
                          entity.risk_level === 'medium' ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {entity.connection_count} connections
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
